import { type HomeSectionId, sectionOfWidget } from "../homeSectionModel.ts"
import type { HomeUnitSpec } from "./homeSectionLayout"
import {
  CASH_FLOW_SLUG,
  NBA_FEED_SLUG,
  PROJECT_STATUS_SLUG
} from "./widgetsResponseAdapter"

/**
 * GC Default's placeable units: what Edit layout moves, resizes and removes.
 *
 * A unit is a CARD, not a backend widget. Several cards compose several
 * widgets (the At a glance band reads four KPIs), and some read none at all
 * (Approvals & signatures, Locations), so a unit has its own id and names the
 * widget(s) it reads separately. Removing a card therefore removes the card
 * and never the data another card still reads — removing Upcoming milestones
 * must not blank the "Milestones this week" count.
 */
export const HOME_UNIT = {
  todayCounts: "home:today_counts",
  priorityActions: "home:priority_actions",
  locations: "home:locations",
  financialMetrics: "home:financial_metrics",
  cashFlow: "home:cash_flow",
  snapshot: "home:snapshot",
  milestones: "home:milestones",
  approvals: "home:approvals",
  pipeline: "home:pipeline",
  activity: "home:activity",
  favoriteReports: "home:favorite_reports"
} as const

export type HomeUnitId = (typeof HOME_UNIT)[keyof typeof HOME_UNIT]

export const isHomeUnitId = (id: string): id is HomeUnitId =>
  (Object.values(HOME_UNIT) as string[]).includes(id)

export const PIPELINE_SLUG = "leads.lead_stages.chart"
export const ACTIVITY_SLUG = "activity_feed..feed"
export const MILESTONES_SLUG = "projects.upcoming_milestones.list"
export const SNAPSHOT_SLUG = "financial.portfolio_financial_snapshot.snapshot"

/**
 * The widget a single-widget card reads. A legacy layout that hid this slug
 * (before cards had ids of their own) still reads as that card removed.
 */
export const BACKING_SLUG: Partial<Record<HomeUnitId, string>> = {
  [HOME_UNIT.priorityActions]: NBA_FEED_SLUG,
  [HOME_UNIT.cashFlow]: CASH_FLOW_SLUG,
  [HOME_UNIT.snapshot]: SNAPSHOT_SLUG,
  [HOME_UNIT.milestones]: MILESTONES_SLUG,
  [HOME_UNIT.pipeline]: PIPELINE_SLUG,
  [HOME_UNIT.activity]: ACTIVITY_SLUG
}

/**
 * Widgets that only feed a composed card. They are never drawn as cards of
 * their own on GC Default — the band, the pipeline card and the Need
 * attention count read them.
 */
export const CONSUMED_SLUGS: ReadonlySet<string> = new Set([
  "projects.active_projects.kpi",
  "projects.delayed_projects.kpi",
  "financial.pending_approvals.kpi",
  "projects.pending_approvals.kpi",
  "financial.revenue_in_window.kpi",
  "financial.outstanding_receivables.kpi",
  "financial.overdue_receivables.kpi",
  "leads.win_rate.kpi",
  PROJECT_STATUS_SLUG,
  ...Object.values(BACKING_SLUG)
])

/**
 * Sizes in grid rows (one row step is 24px: `24h - 16` px tall). Defaults are
 * each card's natural content height, so a section opens without blank space
 * under its shorter card; minimums are where the content stops being
 * readable.
 */
export const HOME_UNIT_SPECS: Record<HomeUnitId, HomeUnitSpec> = {
  [HOME_UNIT.todayCounts]: {
    id: HOME_UNIT.todayCounts,
    section: "today",
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
    autoHeight: true
  },
  [HOME_UNIT.priorityActions]: {
    id: HOME_UNIT.priorityActions,
    section: "today",
    w: 12,
    h: 7,
    minW: 6,
    minH: 4,
    autoHeight: true
  },
  [HOME_UNIT.locations]: {
    id: HOME_UNIT.locations,
    section: "locations",
    w: 12,
    h: 18,
    minW: 8,
    minH: 14
  },
  [HOME_UNIT.financialMetrics]: {
    id: HOME_UNIT.financialMetrics,
    section: "financial",
    w: 12,
    h: 4,
    minW: 6,
    minH: 4,
    autoHeight: true
  },
  [HOME_UNIT.cashFlow]: {
    id: HOME_UNIT.cashFlow,
    section: "financial",
    w: 6,
    h: 13,
    minW: 4,
    minH: 10
  },
  [HOME_UNIT.snapshot]: {
    id: HOME_UNIT.snapshot,
    section: "financial",
    w: 6,
    h: 13,
    minW: 5,
    minH: 10
  },
  [HOME_UNIT.milestones]: {
    id: HOME_UNIT.milestones,
    section: "schedule",
    w: 6,
    h: 11,
    minW: 4,
    minH: 7
  },
  [HOME_UNIT.approvals]: {
    id: HOME_UNIT.approvals,
    section: "schedule",
    w: 6,
    h: 11,
    minW: 4,
    minH: 7
  },
  [HOME_UNIT.pipeline]: {
    id: HOME_UNIT.pipeline,
    section: "pipeline",
    w: 6,
    h: 12,
    minW: 4,
    minH: 9
  },
  [HOME_UNIT.activity]: {
    id: HOME_UNIT.activity,
    section: "pipeline",
    w: 6,
    h: 12,
    minW: 4,
    minH: 7
  },
  [HOME_UNIT.favoriteReports]: {
    id: HOME_UNIT.favoriteReports,
    section: "pipeline",
    w: 12,
    h: 3,
    minW: 4,
    minH: 2,
    autoHeight: true
  }
}

/** The order cards open in, which is also their order inside a section. */
export const HOME_UNIT_ORDER: readonly HomeUnitId[] = [
  HOME_UNIT.todayCounts,
  HOME_UNIT.priorityActions,
  HOME_UNIT.locations,
  HOME_UNIT.financialMetrics,
  HOME_UNIT.cashFlow,
  HOME_UNIT.snapshot,
  HOME_UNIT.milestones,
  HOME_UNIT.approvals,
  HOME_UNIT.pipeline,
  HOME_UNIT.activity,
  HOME_UNIT.favoriteReports
]

const TYPE_SIZE: Record<string, Omit<HomeUnitSpec, "id" | "section">> = {
  kpi: { w: 3, h: 5, minW: 3, minH: 4 },
  progress: { w: 3, h: 5, minW: 3, minH: 4 },
  chart: { w: 6, h: 13, minW: 4, minH: 9 },
  snapshot: { w: 6, h: 13, minW: 5, minH: 10 },
  table: { w: 6, h: 13, minW: 4, minH: 8 },
  list: { w: 6, h: 11, minW: 4, minH: 7 },
  feed: { w: 6, h: 11, minW: 4, minH: 7 },
  alert: { w: 6, h: 11, minW: 4, minH: 7 },
  map: { w: 12, h: 16, minW: 6, minH: 12 }
}

/**
 * Any other catalog widget the user adds lands as a card of its own, in the
 * section its slug reads under (`sectionOfWidget`), at its type's size.
 */
export const genericUnitSpec = (slug: string): HomeUnitSpec => {
  const type = slug.split(".").at(-1) ?? ""
  const size = TYPE_SIZE[type] ?? { w: 6, h: 11, minW: 4, minH: 7 }
  const section: HomeSectionId = sectionOfWidget(slug, { type })
  return { id: slug, section, ...size }
}
